/*
 * Copyright (c) 2022 Certusoft, Inc. All Rights Reserved.
 *
 * This SOURCE CODE FILE, which has been provided by Certusoft as part
 * of a Certusoft product for use ONLY by licensed users of the product,
 * includes CONFIDENTIAL and PROPRIETARY information of Certusoft.
 *
 * USE OF THIS SOFTWARE IS GOVERNED BY THE TERMS AND CONDITIONS
 * OF THE LICENSE STATEMENT AND LIMITED WARRANTY FURNISHED WITH
 * THE PRODUCT.
 */

import * as _ from 'lodash'
import { toLower } from 'lodash'
import * as math from 'mathjs'
import { Matrix } from 'mathjs'

export class MatrixFunctions {
    /*
    Transform component composition/decomposition
    http://nghiaho.com/uploads/code/rotation_matrix_demo.m
   */

    static composeRotation(components: number[], axes: string[] = ['x', 'y', 'z'], intrinsic: boolean = false) {
        axes = _.map(axes, toLower)
        let matrices = []

        for (let i = 0; i < components.length; i++) {
            let mat = math.identity(4, 4) as Matrix
            switch (axes[i]) {
                case 'x':
                    mat.set([5], Math.cos(components[i]))
                    mat.set([6], -Math.sin(components[i]))
                    mat.set([9], Math.sin(components[i]))
                    mat.set([10], Math.cos(components[i]))
                    break
                case 'y':
                    mat.set([0], Math.cos(components[i]))
                    mat.set([2], Math.sin(components[i]))
                    mat.set([8], -Math.sin(components[i]))
                    mat.set([10], Math.cos(components[i]))
                    break
                case 'z':
                    mat.set([0], Math.cos(components[i]))
                    mat.set([1], -Math.sin(components[i]))
                    mat.set([4], Math.sin(components[i]))
                    mat.set([5], Math.cos(components[i]))
                    break
            }
            matrices.push(mat)
        }

        if (intrinsic) {
            return _.reduce(
                matrices,
                function (result, mat) {
                    return math.multiply(result, mat)
                },
                math.identity(4) as Matrix
            )
        }

        return _.reduceRight(
            matrices,
            function (result, mat) {
                return math.multiply(result, mat)
            },
            math.identity(4) as Matrix
        )
    }

    static decomposeRotation(matrix: Matrix, intrinsic: boolean = false) {
        let components: number[] = []
        if (intrinsic) {
            // Intrinsic
            components[0] = Math.atan2(matrix.get([9]), matrix.get([10]))
            components[1] = -Math.asin(matrix.get([8]))
            components[2] = Math.atan2(matrix.get([4]), matrix.get([0]))
        } else {
            // Extrinsic
            components[0] = Math.atan2(-matrix.get([6]), matrix.get([10]))
            components[1] = Math.asin(matrix.get([2]))
            components[2] = Math.atan2(-matrix.get([1]), matrix.get([0]))
        }
        return components
    }

    // Flatten matrix and pad to 4x4
    static format(v: any[][]): any[] {
        _.forEach(v, function (row) {
            row.push(0)
        })
        v.push([0, 0, 0, 1])
        return v.flat()
    }

    /*
    Helpers
   */

    static radToDeg(rad: number) {
        return rad * (180 / Math.PI)
    }

    static degToRad(deg: number) {
        return deg * (Math.PI / 180)
    }

    static clamp(num: number, min: number, max: number) {
        return Math.min(Math.max(num, min), max)
    }

    static normalize(vec: number[]): number[] {
        let len = Math.sqrt(
            _.reduce(
                vec,
                function (acc, x) {
                    return acc + x * x
                },
                0
            )
        )
        return len == 0 ? vec : (math.multiply(vec, 1 / len) as number[])
    }
}
